import React, { useState } from 'react';
import {
  formatBibtex,
  formatAPA,
  formatIEEE,
  formatChicago,
  formatHarvard,
  formatMLA,
  formatMHRA,
  formatVancouver,
  formatMarkdown,
  formatPlainText,
  formatWordXML
} from '../../utils/formatters';
import { generateFilename } from '../../utils/fileUtils';

/**
 * ExportReferences component - Modal for selecting and exporting references
 * in various formats (BibTeX, APA, IEEE, Chicago, Harvard, MLA, MHRA, Vancouver,
 * Markdown, Plain Text, Word XML)
 */
function ExportReferences({ references, onClose, projectName = 'references' }) {
  const [step, setStep] = useState(1); // 1: Select, 2: Format (removed manual ordering)
  const [selectedIds, setSelectedIds] = useState(new Set(references.map(r => r.id)));
  // References are already in correct order from project view, no need for manual reordering
  const [format, setFormat] = useState('bibtex');

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(references.map(r => r.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleExport = () => {
    const selected = references.filter(r => selectedIds.has(r.id));

    let content, filename, mimeType;

    switch (format) {
      case 'bibtex':
        content = formatBibtex(selected);
        filename = generateFilename(projectName, 'bib');
        mimeType = 'application/x-bibtex';
        break;
      case 'plain':
        content = formatPlainText(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'apa':
        content = formatAPA(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'ieee':
        content = formatIEEE(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'chicago':
        content = formatChicago(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'harvard':
        content = formatHarvard(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'mla':
        content = formatMLA(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'mhra':
        content = formatMHRA(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'vancouver':
        content = formatVancouver(selected);
        filename = generateFilename(projectName, 'txt');
        mimeType = 'text/plain';
        break;
      case 'markdown':
        content = formatMarkdown(selected);
        filename = generateFilename(projectName, 'md');
        mimeType = 'text/markdown';
        break;
      case 'word':
        content = formatWordXML(selected);
        filename = generateFilename(projectName, 'xml');
        mimeType = 'application/xml';
        break;
      default:
        content = formatBibtex(selected);
        filename = generateFilename(projectName, 'bib');
        mimeType = 'application/x-bibtex';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    onClose();
  };

  return (
    <div className="export-modal">
      <div className="export-content">
        <div className="export-header">
          <h2>Export References</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>

        <div className="export-steps">
          <div className={`step ${step === 1 ? 'active' : ''}`}>1. Select</div>
          <div className={`step ${step === 2 ? 'active' : ''}`}>2. Format</div>
        </div>

        {step === 1 && (
          <div className="export-step">
            <div className="selection-controls">
              <button onClick={selectAll}>Select All</button>
              <button onClick={deselectAll}>Deselect All</button>
              <span>{selectedIds.size} of {references.length} selected</span>
            </div>
            <div className="reference-selection-list">
              {references.map(ref => (
                <label key={ref.id} className="reference-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(ref.id)}
                    onChange={() => toggleSelection(ref.id)}
                  />
                  <div className="ref-checkbox-content">
                    <strong>{ref.title}</strong>
                    <span>{ref.author} ({ref.year})</span>
                  </div>
                </label>
              ))}
            </div>
            <div className="export-actions">
              <button onClick={onClose}>Cancel</button>
              <button
                onClick={() => setStep(2)}
                disabled={selectedIds.size === 0}
                className="primary-btn"
              >
                Next: Format
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="export-step">
            <p>Choose your export format:</p>
            <div className="format-selection">
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="bibtex"
                  checked={format === 'bibtex'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>BibTeX</strong>
                  <span>Standard bibliography format (.bib)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="word"
                  checked={format === 'word'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>Microsoft Word XML</strong>
                  <span>Import into Word's bibliography manager (.xml)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="apa"
                  checked={format === 'apa'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>APA Style</strong>
                  <span>American Psychological Association format (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="ieee"
                  checked={format === 'ieee'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>IEEE</strong>
                  <span>Institute of Electrical and Electronics Engineers format (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="chicago"
                  checked={format === 'chicago'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>Chicago Style</strong>
                  <span>Chicago Manual of Style format (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="harvard"
                  checked={format === 'harvard'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>Harvard Style</strong>
                  <span>Harvard referencing system (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="mla"
                  checked={format === 'mla'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>MLA Style</strong>
                  <span>Modern Language Association format (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="mhra"
                  checked={format === 'mhra'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>MHRA Style</strong>
                  <span>Modern Humanities Research Association format (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="vancouver"
                  checked={format === 'vancouver'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>Vancouver Style</strong>
                  <span>International Committee of Medical Journal Editors format (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="plain"
                  checked={format === 'plain'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>Plain Text</strong>
                  <span>Simple numbered list (.txt)</span>
                </div>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="markdown"
                  checked={format === 'markdown'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <div>
                  <strong>Markdown</strong>
                  <span>Formatted for Markdown files (.md)</span>
                </div>
              </label>
            </div>
            <div className="export-actions">
              <button onClick={() => setStep(1)}>Back</button>
              <button onClick={handleExport} className="primary-btn">Download</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExportReferences;
